import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Bell, ChevronRight, X } from 'lucide-react';
import StudentShell from './components/StudentShell';
import PageTour from '../TeacherDashboardV3/components/PageTour';
import { shortDate, shortDue, dueEndMs } from '../TeacherDashboardV3/data/unitPlan';
import './sdv3.css';
import {
  DOMAINS, subjectLabel,
  progressOf, sectionScoreRange, totalScoreRange,
  unitTodos,
} from './data/studentMock';
import { useStudentAgenda, useMyProgress, fetchMyHomework, dismissReminder, studentApi } from './data/api';
import { isUnknownAssignmentSlot } from './data/assignmentSlots';
import { isOpenAssessment, sortUpNext } from './data/assessmentOrder';
import { isAssessment } from '../TeacherDashboardV3/data/assessments';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { SubjectPanel } from './StudentSubjectPage';
import { DomainPanel } from './StudentDomainPage';
import { useAuth } from '../Accounts/AuthContext';
import subjectHeroReading from './assets/subject-hero-reading.png';
import subjectHeroMath from './assets/subject-hero-math.png';
import { CubeLoader } from '../pages/LoadingSpinnerPage';
import { DIAGNOSTIC_IDS, diagnosticsForSchool } from '../Diagnostic/diagnosticAvailability';
import { GET_DIAGNOSTIC_SCORE_URL } from '../Diagnostic/diagnosticUrls';
import { bandRange } from '../utils/satScoreBands';

// Home = the whole student surface now (Sandy 2026-08-12): score + agenda
// across the top, then Reading & Writing / Math as TABS whose content is the
// full subject dashboard (SubjectPanel). The subjects are no longer separate
// pages, so a student always lands with the score, what's due, and the
// working path on one screen. Join-a-class lives in the side rail.

const HOME_TOUR = [
  { target: null, title: 'Welcome to Brain Buffs!', body: 'This is your home base. Let me show you around real quick. You can rerun this tour anytime from the ? button in the corner.' },
  { target: 'score', title: 'Your predicted score', body: 'The range you would likely score today, and the range you unlock at your next level. It moves up when you level up, and it never drops from one bad day.' },
  { target: 'week', title: 'Up next', body: 'Everything due, in one list: class assignments and homework, soonest first. Click a row and go.' },
  { target: 'subjects', title: 'Your two subjects', body: 'Reading & Writing and Math live right here in these tabs. Each one shows your path: what to work on first and why, and every level below it.' },
];

// Section heads on the home (Sandy 2026-08-12): real headings in ink, not
// tiny gray eyebrows — "Predicted score" and "Up next" should read first.
const HEAD = 'text-[17px] font-semibold text-ink tracking-tight';

// ---------- Up next: class-unit slots + homework sets, ONE list ----------

function domainFull(domainKey) {
  for (const subject of ['math', 'reading']) {
    const d = DOMAINS[subject].find((x) => x.key === domainKey);
    if (d) return d.full;
  }
  return null;
}

function upNextItems({ real, homework, demo }) {
  const items = [];
  // Unit slots (incl. placement walks) and homework sets, placed or not —
  // the diagnostic no longer gates this list (Sandy 2026-09-02).
  const todos = real ? (real.nextTodos || real.todos) : (demo ? unitTodos().filter((a) => !a.past).slice(0, 1) : []);
  for (const a of todos) {
    items.push({
      key: a.key,
      title: a.type,
      sub: a.domainKey ? domainFull(a.domainKey) : null,
      subject: a.subject,
      dueRaw: a.dueRaw || a.due,
      end: a.dueRaw || a.due ? dueEndMs(a.dueRaw || a.due) : Infinity,
      past: !!a.past,
      // A placement-gated unit's row opens the walk itself, not the domain
      // page — one tap from "Find your level" to the first question.
      to: a.placement && a.domainKey ? `/placement/${a.domainKey}`
        : a.subject && a.domainKey ? `/student-dashboard/${a.subject}/${a.domainKey}` : '/student-dashboard',
    });
  }
  // Closed sets (2026-10-08) are review-only: they live on the homework page, not up next.
  for (const h of (homework || []).filter((x) => !x.done && !x.closed)) {
    // Curriculum assessments (2026-10-18) say so on their row.
    const kind = isAssessment(h) ? 'Assessment' : 'Homework';
    items.push({
      key: `hw-${h.classID}-${h.hwId}`,
      title: h.title,
      // an assessment owes no walkthrough (2026-10-26)
      sub: h.walksLeft > 0 && h.answered >= h.count && !isAssessment(h)
        ? `${kind} · ${h.walksLeft} ${h.walksLeft === 1 ? 'walkthrough' : 'walkthroughs'} left`
        : h.answered > 0 ? `${kind} · ${h.answered}/${h.count} done` : kind,
      subject: null,
      dueRaw: h.dueDate || null,
      end: h.dueDate ? dueEndMs(h.dueDate) : Infinity,
      past: h.dueDate ? dueEndMs(h.dueDate) < Date.now() : false,
      openAssessment: isOpenAssessment(h),
      to: `/student-homework/${h.classID}/${h.hwId}`,
    });
  }
  // Open assessments first (today's in-class one must not hide under old
  // overdue sets), then the usual soonest-due order.
  return sortUpNext(items);
}

function UpNextRow({ item }) {
  return (
    <Link
      to={item.to}
      className="group flex items-center gap-3 rounded-[14px] bg-neutral-50 hover:bg-neutral-100 px-4 py-3 no-underline transition-colors"
    >
      {/* The due date sits UNDER the title (2026-10-09 audit): beside it, a long
          "Overdue · Sat, Aug 1, 11:59 PM" left the title 52px at 375 and 105px at 1718. */}
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-bold text-ink leading-snug truncate">{item.title}</span>
        {item.sub && (
          <span className="block mt-0.5 text-[12px] font-medium text-neutral-500 truncate">{item.sub}</span>
        )}
        {item.dueRaw && (
          <span className={'block mt-0.5 text-[12px] font-semibold tabular-nums truncate ' + (item.past ? 'text-error-600' : 'text-neutral-500')}>
            {item.past ? 'Overdue · ' : 'Due '}{shortDue(item.dueRaw)}
          </span>
        )}
      </span>
      <ChevronRight size={15} strokeWidth={2.5} className="flex-none text-neutral-300 group-hover:text-ink transition-colors" />
    </Link>
  );
}

// A teacher nudge above everything: bell, message, timestamp, X clears it.
function ReminderRow({ reminder, onDismiss }) {
  const { user, handleAPIResponse } = useAuth();
  const at = reminder.at ? new Date(reminder.at) : null;
  const dismiss = () => {
    onDismiss(reminder.classID);
    dismissReminder({ user, handleAPIResponse }, reminder.classID).catch(() => {});
  };
  return (
    <div className="flex items-start gap-3 rounded-[14px] bg-neutral-50 px-5 py-3.5 mb-4">
      <Bell size={15} strokeWidth={2} className="flex-none text-gold-500 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] text-neutral-700 leading-snug">
          {reminder.message || 'Your teacher sent a reminder to keep working'}
        </div>
        {at && <div className="mt-0.5 text-[12px] font-medium text-neutral-400">{shortDate(at)}</div>}
      </div>
      <button
        type="button"
        aria-label="Dismiss reminder"
        onClick={dismiss}
        className="flex-none w-6 h-6 rounded-[6px] border-0 bg-transparent hover:bg-neutral-200 text-neutral-400 hover:text-ink cursor-pointer flex items-center justify-center p-0"
      >
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ---------- The goal: your range drawn on the real score curve ----------

// Subject bands from real progress (placed domains only) or the demo mock.
function bandsOf(subject, realProgress, signedIn) {
  if (realProgress) {
    return DOMAINS[subject]
      .map((d) => realProgress.domains?.[d.key]?.band)
      .filter((b) => b != null);
  }
  return signedIn ? [] : DOMAINS[subject].map((d) => progressOf(d.key).level);
}

// A subject's predicted section range: the College Board band of its lowest level.
const rangeAt = (subject, bands, bump = 0) =>
  bandRange(subject, Math.min(7, (bands.length ? Math.min(...bands) : 1) + bump));

// The national SAT total-score distribution (mean ~1050, SD ~217, College
// Board 2023 cohort) — used ONLY for the hover percentile readout. The drawn
// curve is the STUDENT'S own predicted distribution, not this one.
const CURVE = { MIN: 400, MAX: 1600, MU: 1050, SIGMA: 217 };

function erf(x) {
  // Abramowitz & Stegun 7.1.26 — plenty for a percentile label.
  const s = x < 0 ? -1 : 1;
  const a = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * a);
  const y = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-a * a);
  return s * y;
}
const percentileOf = (v) =>
  Math.min(99, Math.max(1, Math.round(50 * (1 + erf((v - CURVE.MU) / (CURVE.SIGMA * Math.SQRT2))))));

const ordinal = (n) => {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][Math.min(n % 10, 4)] || 'th'}`;
};

// The score hero: the student's OWN bell curve on the 400-1600 axis (Sandy
// 2026-08-12 v2). The curve peaks at their expected score and its spread IS
// their range — the shape alone points at the score, no marker line, no
// fills, one black line. The next-level range sits as a quiet dashed marker
// to the right; hovering reads out any score's national percentile.
function ScoreCurve({ range, milestone, goalScore }) {
  const [hover, setHover] = useState(null); // score under the cursor, in tens
  const { MIN, MAX } = CURVE;
  const W = 600;
  const H = 150;
  const BASE = H - 6; // baseline y
  const AMP = H - 26; // curve amplitude above the baseline

  const mid = Math.round((range[0] + range[1]) / 2 / 10) * 10;
  const halfW = Math.round((range[1] - range[0]) / 2 / 10) * 10;
  // The rainbow bell is the student's GOAL once they've set one (Sandy
  // 2026-08-18) — same width as their own bell, centered on the goal score.
  // Without a goal it falls back to the next-level milestone range.
  const target = goalScore != null ? [goalScore - halfW, goalScore + halfW] : milestone;
  const mMid = target ? Math.round((target[0] + target[1]) / 2 / 10) * 10 : null;

  // Hovering slides the student's whole bell along the axis — and the motion
  // is SPRUNG, not snapped (Sandy 2026-08-12 v4): a rAF loop eases the peak
  // toward the mouse, and back home after the mouse leaves, so the curve
  // rolls like a wave instead of teleporting. Reduced motion snaps.
  const [displayMid, setDisplayMid] = useState(mid);
  const dispRef = useRef(mid);
  useEffect(() => {
    const target = hover != null ? hover : mid;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { dispRef.current = target; setDisplayMid(target); return undefined; }
    let raf;
    const step = () => {
      const d = dispRef.current;
      const next = d + (target - d) * 0.11;
      if (Math.abs(target - next) < 0.4) {
        dispRef.current = target;
        setDisplayMid(target);
        return;
      }
      dispRef.current = next;
      setDisplayMid(next);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [hover, mid]);
  const shownMid = Math.round(displayMid / 10) * 10;
  const dispRange = [shownMid - halfW, shownMid + halfW];
  // At (or past) the goal curve, the moment goes rainbow: the goal bell
  // un-fades and the moving number wears the gradient.
  const reached = target != null && displayMid >= target[0];

  const xOf = (v) => ((v - MIN) / (MAX - MIN)) * W;
  const pctOf = (v) => `${(((v - MIN) / (MAX - MIN)) * 100).toFixed(2)}%`;

  // ONE spread for both bells (Sandy 2026-08-12: the goal curve must be the
  // same width as the student's) — from the student's range, edges ~1.5 SD.
  const sig = Math.max(35, (range[1] - range[0]) / 3);
  const pathFor = (mu, amp) => {
    const pts = [];
    for (let v = MIN; v <= MAX; v += 5) {
      const y = BASE - Math.exp(-((v - mu) ** 2) / (2 * sig * sig)) * amp;
      pts.push(`${xOf(v).toFixed(1)},${y.toFixed(1)}`);
    }
    return `M ${pts.join(' L ')}`;
  };
  const curvePath = pathFor(displayMid, AMP);
  // The goal curve: same bell, one step right, unlabeled — it's a place on
  // the axis, not a stat. Faded until the student's peak reaches it.
  const nextPath = mMid != null && mMid !== mid ? pathFor(mMid, AMP) : null;
  // Each rainbow spans just its bump (~±3 SD), not the whole axis —
  // otherwise a bump lands on a single hue of the gradient. The main curve's
  // gradient rides its moving peak.
  const nextGradX = nextPath ? [xOf(mMid - 3 * sig), xOf(mMid + 3 * sig)] : null;
  const mainGradX = [xOf(displayMid - 3 * sig), xOf(displayMid + 3 * sig)];

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const v = Math.round((MIN + frac * (MAX - MIN)) / 10) * 10;
    // keep the whole range on the 400-1600 axis
    setHover(Math.min(MAX - halfW, Math.max(MIN + halfW, v)));
  };

  return (
    <div className="mt-2">
      {/* label deck above the curve: the range rides the peak — which is
          wherever the mouse is while exploring, home otherwise. */}
      <div className="relative h-[48px] xl:h-[56px]">
        <div
          className="absolute bottom-0 -translate-x-1/2 text-center whitespace-nowrap"
          style={{ left: `clamp(80px, ${pctOf(displayMid)}, calc(100% - 80px))` }}
        >
          <div className={
            'text-[26px] xl:text-[32px] font-extrabold leading-none tracking-tight tabular-nums '
            + (reached ? 'sdv3-grad-text' : 'text-ink')
          }>
            {dispRange[0]}{!reached && <span className="text-neutral-300">–</span>}{reached && '–'}{dispRange[1]}
          </div>
          <div className="mt-0.5 text-[11px] font-semibold text-neutral-400 tabular-nums">
            {ordinal(percentileOf(dispRange[0]))}–{ordinal(percentileOf(dispRange[1]))} percentile
          </div>
        </div>
      </div>

      <div
        className="relative cursor-crosshair"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block w-full h-[96px] xl:h-[140px]">
          <defs>
            {/* the brand rainbow, faded, for the goal curve */}
            <linearGradient
              id="bbNextLevelGrad"
              gradientUnits="userSpaceOnUse"
              x1={nextGradX ? nextGradX[0] : 0}
              y1="0"
              x2={nextGradX ? nextGradX[1] : W}
              y2="0"
            >
              <stop offset="0%" stopColor="#a960ee" />
              <stop offset="33%" stopColor="#ff333d" />
              <stop offset="66%" stopColor="#ffcb57" />
              <stop offset="100%" stopColor="#90e0ff" />
            </linearGradient>
            {/* the same rainbow riding the student's moving bump — the black
                curve wears this the moment it reaches the goal */}
            <linearGradient
              id="bbMainGrad"
              gradientUnits="userSpaceOnUse"
              x1={mainGradX[0]}
              y1="0"
              x2={mainGradX[1]}
              y2="0"
            >
              <stop offset="0%" stopColor="#a960ee" />
              <stop offset="33%" stopColor="#ff333d" />
              <stop offset="66%" stopColor="#ffcb57" />
              <stop offset="100%" stopColor="#90e0ff" />
            </linearGradient>
          </defs>
          {/* baseline axis */}
          <line x1="0" y1={BASE} x2={W} y2={BASE} stroke="#e5e5e5" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          {/* the goal: the same bell one step right, in the faded brand
              rainbow — unlabeled, it un-fades when your peak arrives */}
          {nextPath && (
            <path
              d={nextPath}
              fill="none"
              stroke="url(#bbNextLevelGrad)"
              strokeOpacity={reached ? 1 : 0.28}
              strokeWidth={reached ? 2.5 : 2}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              style={{ transition: 'stroke-opacity 300ms ease' }}
            />
          )}
          {/* the student's curve: one black line, nothing under it — and it
              goes rainbow the moment it reaches the goal */}
          <path d={curvePath} fill="none" stroke={reached ? 'url(#bbMainGrad)' : '#141414'} strokeWidth="2.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>

      {/* axis ends */}
      <div className="flex justify-between mt-1 text-[11.5px] font-semibold text-neutral-400 tabular-nums">
        <span>400</span>
        <span>1600</span>
      </div>
    </div>
  );
}

// The "Set goal score" pill: school signups skip the /start quiz where the
// goal is set, so the score hero offers it here (Sandy 2026-08-14). Gradient
// outline = the sanctioned goal/progress treatment.
function GoalPill({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="sdv3-grad-outline inline-flex items-center h-8 px-3.5 rounded-full text-[12.5px] font-bold text-ink cursor-pointer"
    >
      Set goal score
    </button>
  );
}

// Goal setter: the same 400-1600 slider the diagnostic intro used. Saves to
// the NEW stack (ClaudeUsers goalScore via claude-student-api setGoal,
// 2026-08-18); onSaved tells the page so the pill disappears without a refetch.
function SetGoalModal({ onClose, onSaved }) {
  const { user, handleAPIResponse } = useAuth();
  const [value, setValue] = useState(1200);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pct = ((value - 400) / 1200) * 100;

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await studentApi({ user, handleAPIResponse }, 'setGoal', { goalScore: value });
      if (onSaved) onSaved(value);
      onClose();
    } catch (err) {
      // Admin view mode refuses the save: say so, not "try again".
      setError(err?.viewOnly ? err.message : 'Could not save your goal. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-[400px] bg-white rounded-[20px] shadow-[0_24px_64px_rgba(0,0,0,.18)] px-7 py-8">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-4 right-4 w-7 h-7 rounded-full border-0 bg-neutral-100 hover:bg-neutral-200 text-neutral-500 hover:text-ink cursor-pointer flex items-center justify-center p-0"
        >
          <X size={15} strokeWidth={2.5} />
        </button>
        <div className="text-[19px] font-extrabold text-ink">Set your goal score</div>
        <p className="m-0 mt-1.5 text-[13.5px] text-neutral-600 leading-relaxed">
          The SAT score you're working toward. Your goal curve sits on your chart, and your number goes rainbow when you reach it.
        </p>
        <div className="mt-6 text-center text-[38px] font-extrabold text-ink tabular-nums leading-none">{value}</div>
        <input
          type="range"
          min="400"
          max="1600"
          step="10"
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          className="sdv3-goal-slider mt-5 w-full h-2 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, #a960ee 0%, #ff333d ${(pct * 0.33).toFixed(1)}%, #ffcb57 ${(pct * 0.66).toFixed(1)}%, #90e0ff ${pct.toFixed(1)}%, #e5e5e5 ${pct.toFixed(1)}%, #e5e5e5 100%)`,
          }}
        />
        <div className="mt-1 flex justify-between text-[11.5px] font-semibold text-neutral-400 tabular-nums">
          <span>400</span>
          <span>1600</span>
        </div>
        {error && <p className="m-0 mt-3 text-[12.5px] font-semibold text-error-600">{error}</p>}
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="mt-6 w-full h-11 rounded-full border-0 bg-ink hover:bg-neutral-800 text-white text-[14.5px] font-semibold cursor-pointer disabled:bg-neutral-300"
        >
          {busy ? 'Saving…' : 'Save goal'}
        </button>
      </div>
    </div>,
    document.body
  );
}

function GoalSection({ realProgress, signedIn, showGoalPill, onSetGoal, goalScore }) {
  const failed = signedIn && !realProgress;
  const rBands = bandsOf('reading', realProgress, signedIn);
  const mBands = bandsOf('math', realProgress, signedIn);

  const reading = realProgress ? rangeAt('reading', rBands) : (!signedIn ? sectionScoreRange('reading') : null);
  const math = realProgress ? rangeAt('math', mBands) : (!signedIn ? sectionScoreRange('math') : null);
  const total = realProgress
    ? [reading[0] + math[0], reading[1] + math[1]]
    : (!signedIn ? totalScoreRange() : null);

  // The promise: the range you unlock when your lowest-level areas reach the
  // next level. Same rule as the range itself (subject range = its lowest
  // band), so the milestone is honest, not a hype number.
  const milestone = realProgress
    ? [rangeAt('reading', rBands, 1)[0] + rangeAt('math', mBands, 1)[0], rangeAt('reading', rBands, 1)[1] + rangeAt('math', mBands, 1)[1]]
    : null; // the signed-out demo shows no milestone
  const showMilestone = milestone && total && milestone[1] > total[1];

  return (
    <section className="flex-1 min-w-0" data-tour="score">
      <div className="flex items-center gap-3 flex-wrap">
        <div className={HEAD}>Predicted score</div>
        {showGoalPill && <GoalPill onClick={onSetGoal} />}
      </div>
      {failed ? (
        <p className="m-0 mt-3 text-[15px] text-neutral-500 leading-relaxed">
          We couldn't load your score right now. Refresh to try again.
        </p>
      ) : (
        <ScoreCurve range={total} milestone={showMilestone ? milestone : null} goalScore={goalScore} />
      )}
    </section>
  );
}

// ---------- Unplaced: the same score hero, previewed ----------

// Before the diagnostic, the score hero shows WHAT IT WILL BE (Sandy
// 2026-08-12): a dashed ghost bell where their score will land and the faded
// rainbow goal bell it attaches to, in the exact frame the real curve uses —
// plus the one CTA. The tabs below stay visible (grayed) so the student sees
// what they'll be working with.
function DiagnosticHero({ started, showGoalPill, onSetGoal }) {
  const { MIN, MAX } = CURVE;
  const W = 600;
  const H = 150;
  const BASE = H - 6;
  const AMP = H - 26;
  const sig = 60;
  const xOf = (v) => ((v - MIN) / (MAX - MIN)) * W;
  const pctOf = (v) => `${(((v - MIN) / (MAX - MIN)) * 100).toFixed(2)}%`;
  const pathFor = (mu, amp) => {
    const pts = [];
    for (let v = MIN; v <= MAX; v += 5) {
      const y = BASE - Math.exp(-((v - mu) ** 2) / (2 * sig * sig)) * amp;
      pts.push(`${xOf(v).toFixed(1)},${y.toFixed(1)}`);
    }
    return `M ${pts.join(' L ')}`;
  };
  const ghostMu = 950;
  const goalMu = 1180;
  return (
    <section className="relative flex-1 min-w-0" data-tour="score">
      <div className="flex items-center gap-3 flex-wrap">
        <div className={HEAD}>Predicted score</div>
        {showGoalPill && <GoalPill onClick={onSetGoal} />}
      </div>
      {/* label deck, same frame as the real hero: quiet tags over each peak */}
      <div className="relative h-[30px]">
        <div
          className="absolute bottom-1 -translate-x-1/2 text-[11px] font-bold text-neutral-400 whitespace-nowrap"
          style={{ left: pctOf(ghostMu) }}
        >
          your score
        </div>
        <div
          className="absolute bottom-1 -translate-x-1/2 sdv3-grad-text text-[11px] font-extrabold whitespace-nowrap"
          style={{ left: pctOf(goalMu) }}
        >
          your goal
        </div>
      </div>
      <div className="cursor-default">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block w-full h-[96px] xl:h-[140px]">
          <defs>
            <linearGradient
              id="bbGhostGoalGrad"
              gradientUnits="userSpaceOnUse"
              x1={xOf(goalMu - 3 * sig)}
              y1="0"
              x2={xOf(goalMu + 3 * sig)}
              y2="0"
            >
              <stop offset="0%" stopColor="#a960ee" />
              <stop offset="33%" stopColor="#ff333d" />
              <stop offset="66%" stopColor="#ffcb57" />
              <stop offset="100%" stopColor="#90e0ff" />
            </linearGradient>
          </defs>
          <line x1="0" y1={BASE} x2={W} y2={BASE} stroke="#e5e5e5" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          {/* the goal bell, faded rainbow — same as the real hero */}
          <path d={pathFor(goalMu, AMP)} fill="none" stroke="url(#bbGhostGoalGrad)" strokeOpacity="0.28" strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          {/* the ghost of the student's bell: dashed until the diagnostic draws it */}
          <path d={pathFor(ghostMu, AMP)} fill="none" stroke="#c4c4c4" strokeWidth="2.5" strokeDasharray="6 6" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>
      <div className="flex justify-between mt-1 text-[11.5px] font-semibold text-neutral-400 tabular-nums">
        <span>400</span>
        <span>1600</span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Link
          to="/diagnostic"
          className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-ink hover:bg-neutral-800 text-white text-[14px] font-semibold no-underline transition-colors"
        >
          {started ? 'Resume the diagnostic' : 'Start the diagnostic'} <ArrowRight size={15} strokeWidth={2.5} />
        </Link>
        {/* The "pick up where you left off" line was cut (Sandy 2026-08-23).
            The button already says Resume. */}
        {!started && (
          <p className="m-0 text-[13px] text-neutral-500 leading-snug">
            The diagnostic draws your curve and sets your goal.
          </p>
        )}
      </div>
    </section>
  );
}

// ---------- Under the subject switch: that subject's own range ----------

// Sandy 2026-09-23: the chosen subject's predicted range, in the type of the
// old per-subject lockup (cut 2026-08-23). A subject with no levels shows
// nothing, except mid-diagnostic with the other subject done: then it offers
// the way back into the test. The domain cards keep the placement quiz.
function SubjectScoreLine({ subject, bands, offerDiagnostic }) {
  if (bands.length) {
    const [lo, hi] = rangeAt(subject, bands);
    return (
      <div className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">
          Predicted {subjectLabel(subject)} score
        </div>
        <div className="text-[22px] font-extrabold leading-tight tabular-nums text-ink">{lo}–{hi}</div>
      </div>
    );
  }
  if (!offerDiagnostic) return null;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
      <Link
        to="/diagnostic"
        className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-ink hover:bg-neutral-800 text-white text-[14px] font-semibold no-underline transition-colors"
      >
        Resume the diagnostic <ArrowRight size={15} strokeWidth={2.5} />
      </Link>
      <p className="m-0 text-[13px] text-neutral-500 leading-snug">
        Or find your level in each area below with a short quiz.
      </p>
    </div>
  );
}

// One spring for the whole switch, so the sliding background, the widening art
// slot and the button growing around it all move as a single object instead of
// three things that happen to change at once.
const SWITCH_SPRING = { type: 'spring', stiffness: 420, damping: 34, mass: 0.7 };

// The subject art, shown on whichever tab is chosen.
const SUBJECT_HERO = { reading: subjectHeroReading, math: subjectHeroMath };

export default function StudentHomePage() {
  const motionOff = useReducedMotion();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { user, handleAPIResponse, viewAs } = useAuth();
  const { data: agendaData, loading: agendaLoading } = useStudentAgenda();
  const { data: realProgress, loading: progressLoading, error: progressError } = useMyProgress();
  const noSchool = agendaData?.school === 'No School';
  const real = agendaData?.loaded ? agendaData : null;

  // Homework sets (signed in only; the signed-out demo shows nothing here).
  const [homework, setHomework] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!user) { setHomework(null); return undefined; }
    fetchMyHomework({ user, handleAPIResponse })
      .then((items) => { if (alive) setHomework(items); })
      .catch(() => { if (alive) setHomework(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [dismissed, setDismissed] = useState(() => new Set());
  const reminders = (real?.reminders || []).filter((r) => !dismissed.has(r.classID));

  // Diagnostic gate: an unplaced student does the diagnostic FIRST.
  // ?unplaced=1 on the signed-out demo previews the pre-diagnostic state
  // (same switch the old subject pages had).
  const unplaced = (!!user && !!realProgress && !realProgress.placed)
    || (!user && new URLSearchParams(search).get('unplaced') === '1');
  // English now, Math later (Sandy 2026-09-23): a student with levels in only
  // one subject has no real total yet. The hero keeps its pre-diagnostic
  // state until both subjects have levels; each tab shows its own range.
  const leveled = {
    reading: !!realProgress && bandsOf('reading', realProgress, true).length > 0,
    math: !!realProgress && bandsOf('math', realProgress, true).length > 0,
  };
  const partial = !!user && !!realProgress && !unplaced && !(leveled.reading && leveled.math);

  // "Set goal score" pill (Sandy 2026-08-14): school signups skip the /start
  // quiz where the goal is chosen, so a signed-in school student with no
  // goal gets the pill next to the score heading. Fully NEW-STACK gating
  // (Sandy 2026-08-18): school AND goal both come from the ClaudeUsers row
  // (via getAssignments) — a legacy UserDatabase GoalScore/School changes
  // nothing here. justSetGoal hides the pill immediately after saving, since
  // the agenda data isn't refetched.
  const [justSetGoal, setJustSetGoal] = useState(null);
  const newStackSchool = agendaData?.school || '';
  const goalScore = justSetGoal ?? agendaData?.goalScore ?? null;
  const showGoalPill = !!user && !!newStackSchool && newStackSchool !== 'No School' && goalScore == null;
  const [goalOpen, setGoalOpen] = useState(false);

  // Start vs Resume (Sandy 2026-08-14): the hero's pill says Resume when a
  // diagnostic session is already In Progress — same status probe the
  // diagnostic intro page uses. Skipped in admin view mode: the probe
  // identifies by the signed-in token, which is the admin's.
  const [diagStarted, setDiagStarted] = useState(false);
  useEffect(() => {
    if (!user || viewAs || !(unplaced || partial)) { setDiagStarted(false); return undefined; }
    let alive = true;
    const statusOf = async (assignmentId) => {
      try {
        const body = { diagnosticProgress: true, assignmentId };
        const res = await fetch(GET_DIAGNOSTIC_SCORE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: user.accessToken },
          body: JSON.stringify(body),
        });
        const handled = await handleAPIResponse(res, body);
        if (!handled.ok) return null;
        const data = await handled.json();
        return data.status || null;
      } catch { return null; }
    };
    // Only the diagnostics this school can see (Sandy 2026-09-16): a
    // year-old diagnostic-2 session must not turn the pill into "Resume" for
    // a test the intro page doesn't offer. Every one is probed, since a
    // Diagnostic 3 first school shows all three once diagnostic-4 is done.
    Promise.all(DIAGNOSTIC_IDS.map(statusOf)).then((statuses) => {
      if (!alive) return;
      const done = DIAGNOSTIC_IDS.filter((id, i) => statuses[i] === 'Done');
      const shown = diagnosticsForSchool(agendaData?.school || '', '', done).map((d) => d.id);
      setDiagStarted(DIAGNOSTIC_IDS.some((id, i) => shown.includes(id) && statuses[i] === 'In Progress'));
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, unplaced, partial, agendaData?.school]);

  const due = useMemo(
    () => upNextItems({ real, homework: real ? homework : null, demo: !user }),
    [real, homework, user]
  );
  const shownDue = due.slice(0, 4);
  const moreDue = due.length - shownDue.length;

  // The subject tabs. URL is the source of truth (?tab=reading|math) so the
  // old /student-dashboard/:subject links can redirect straight into a tab.
  // Default: the subject of the first thing due, else the lower-band subject.
  const tabParam = new URLSearchParams(search).get('tab');
  const defaultTab = useMemo(() => {
    const firstDue = due.find((d) => d.subject);
    if (firstDue) return firstDue.subject;
    // The tab the student last used wins over the band heuristic: reloading
    // mid-Reading-session used to land on Math just because its band was
    // lower (QA 2026-08-27).
    try {
      const last = window.localStorage.getItem('bbLastSubjectTab');
      if (DOMAINS[last]) return last;
    } catch { /* storage off */ }
    const minOf = (s) => {
      const b = bandsOf(s, realProgress, !!user);
      return b.length ? Math.min(...b) : Infinity;
    };
    return minOf('math') < minOf('reading') ? 'math' : 'reading';
  }, [due, realProgress, user]);
  const tab = DOMAINS[tabParam] ? tabParam : defaultTab;
  useEffect(() => {
    try { window.localStorage.setItem('bbLastSubjectTab', tab); } catch { /* storage off */ }
  }, [tab]);
  const setTab = (s) => navigate(`/student-dashboard?tab=${s}`, { replace: true });

  // A domain param swaps the subject path for that domain's roadmap, right
  // under the same tab bar (Sandy 2026-08-12): the road is part of the
  // dashboard now, not a separate page. Switching tabs clears it.
  const domainParam = new URLSearchParams(search).get('domain');
  const openDomain = DOMAINS[tab]?.some((d) => d.key === domainParam) ? domainParam : null;

  // A subject's segment wears a small "Due" badge when something in the Up
  // next list belongs to it (Sandy 2026-08-12).
  const dueIn = (s) => due.some((d) => d.subject === s);

  if (user && (agendaLoading || progressLoading)) {
    return (
      <StudentShell>
        <div className="flex items-center justify-center min-h-[70vh]">
          <CubeLoader size={140} />
        </div>
      </StudentShell>
    );
  }

  // A domain open = a full-screen takeover (Sandy 2026-08-12): no score, no
  // Up next, no tabs — just the road and the way back.
  if (openDomain && !unplaced) {
    return (
      <StudentShell>
        <div className="px-4 sm:px-6 pt-6 pb-14 max-w-[1100px] w-full mx-auto">
          <button
            type="button"
            onClick={() => setTab(tab)}
            className="mb-4 p-0 border-0 bg-transparent text-[13.5px] font-semibold text-neutral-500 hover:text-ink cursor-pointer transition-colors"
          >
            ← Back to {subjectLabel(tab)}
          </button>
          <DomainPanel key={`${tab}-${openDomain}`} subject={tab} domainKey={openDomain} />
        </div>
      </StudentShell>
    );
  }

  return (
    <StudentShell>
      <div className="px-6 pt-6 pb-14 max-w-[1100px] w-full mx-auto">
        {reminders.map((r) => (
          <ReminderRow
            key={r.classID}
            reminder={r}
            onDismiss={(classID) => setDismissed((d) => new Set(d).add(classID))}
          />
        ))}

        {/* Top: the score (why you're here) and the agenda (what's due), one row. */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-14 items-start">
          {unplaced || partial ? (
            <DiagnosticHero started={diagStarted} showGoalPill={showGoalPill} onSetGoal={() => setGoalOpen(true)} />
          ) : (
            <GoalSection realProgress={realProgress} signedIn={!!user} showGoalPill={showGoalPill} onSetGoal={() => setGoalOpen(true)} goalScore={goalScore} />
          )}
          <section className="w-full lg:w-[380px] lg:flex-none" data-tour="week">
            <div className="flex items-baseline justify-between">
              <div className={HEAD}>Up next</div>
              {user && !noSchool && (!unplaced || due.length > 0) && (
                <Link to="/student-dashboard/homework" className="text-[12.5px] font-semibold text-neutral-500 hover:text-ink no-underline transition-colors">
                  All homework →
                </Link>
              )}
            </div>
            <div className="mt-3 flex flex-col gap-2">
              {/* Teacher homework only; the diagnostic has its own entry point
                  and no longer leads this list (Sandy 2026-09-02). */}
              {shownDue.length === 0 ? (
                <p className="m-0 text-[14px] text-neutral-400 leading-relaxed">{real?.assignments?.some((assignment) => (assignment.slots || []).some(isUnknownAssignmentSlot)) ? 'Some checkpoint results are unavailable. We could not confirm your next assignment.' : 'Nothing due right now.'}</p>
              ) : (
                shownDue.map((item) => <UpNextRow key={item.key} item={item} />)
              )}
              {moreDue > 0 && (
                <Link to="/student-dashboard/homework" className="text-[13px] font-semibold text-neutral-500 hover:text-ink no-underline px-1 transition-colors">
                  {moreDue} more →
                </Link>
              )}
            </div>
          </section>
        </div>

        {goalOpen && <SetGoalModal onClose={() => setGoalOpen(false)} onSaved={setJustSetGoal} />}

        {/* The subjects, as tabs. Each tab IS the old subject dashboard —
            visible even before the diagnostic (grayed), so the student sees
            what they'll be working with. */}
        {(
          <div className="mt-7" data-tour="subjects">
            {/* The segmented pill stays (Sandy 2026-08-23: "still the tab switch
                for math and reading, not much bigger"). The only change is the
                subject art riding the LEFT of whichever tab is chosen, which is
                where the picture went when the panel below lost its score lockup.
                h-10 to h-12 is all the extra height the art needs. */}
            {/* Phones: the pill spans the row and the two tabs split it evenly -
                the inline version ran past the right screen edge (Sandy 2026-08-18). */}
            <div className="flex w-full sm:w-auto sm:inline-flex gap-1 rounded-full bg-neutral-100 p-1">
              {['reading', 'math'].map((s) => {
                const on = tab === s;
                return (
                  <motion.button
                    key={s}
                    type="button"
                    layout
                    transition={motionOff ? { duration: 0 } : SWITCH_SPRING}
                    onClick={() => setTab(s)}
                    className={
                      'relative flex flex-1 sm:flex-initial items-center justify-center gap-2 h-12 px-3 sm:px-5 rounded-full border-0 bg-transparent cursor-pointer text-[14.5px] font-semibold whitespace-nowrap '
                      + (on ? 'text-ink' : 'text-neutral-500 hover:text-ink')
                    }
                  >
                    {/* the white chip is ONE element that slides between tabs
                        (shared layoutId), instead of two that flip on and off */}
                    {on && (
                      <motion.span
                        layoutId="sdv3-subject-chip"
                        className="absolute inset-0 rounded-full bg-white shadow-sm"
                        transition={motionOff ? { duration: 0 } : SWITCH_SPRING}
                      />
                    )}

                    {/* the art opens a slot for itself rather than popping in and
                        shoving the label sideways */}
                    <AnimatePresence initial={false}>
                      {on && (
                        <motion.span
                          key="art"
                          className="relative z-10 flex-none overflow-hidden"
                          initial={{ width: 0, opacity: 0 }}
                          animate={{ width: 32, opacity: 1 }}
                          exit={{ width: 0, opacity: 0 }}
                          transition={motionOff ? { duration: 0 } : SWITCH_SPRING}
                        >
                          <img src={SUBJECT_HERO[s]} alt="" className="h-8 w-8 object-contain" />
                        </motion.span>
                      )}
                    </AnimatePresence>

                    <span className="relative z-10">{subjectLabel(s)}</span>
                    {dueIn(s) && (
                      <span
                        className="relative z-10 flex-none inline-flex items-center rounded-full bg-primary-500 text-white text-[10.5px] font-semibold leading-none px-2 py-[5px]"
                        title="You have work due here"
                      >
                        Due
                      </span>
                    )}
                  </motion.button>
                );
              })}
            </div>
            {!!user && !!realProgress && !unplaced && (
              <SubjectScoreLine
                subject={tab}
                bands={bandsOf(tab, realProgress, true)}
                offerDiagnostic={diagStarted && leveled[tab === 'reading' ? 'math' : 'reading']}
              />
            )}
            <div className="pt-3">
              {/* the panel crossfades with the switch: swapping it instantly
                  made the chip look like it slid onto already-changed content */}
              <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={`${tab}${unplaced ? '-unplaced' : ''}-wrap`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: motionOff ? 0 : 0.18 }}
              >
              <SubjectPanel
                key={`${tab}${unplaced ? '-unplaced' : ''}`}
                subject={tab}
                real={realProgress}
                agenda={agendaData}
                progressError={progressError}
                unplaced={unplaced}
              />
              </motion.div>
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
      <PageTour pageKey="student-home" steps={HOME_TOUR} />
    </StudentShell>
  );
}
